import { UserMatchingRequestDto } from "@api-types/user/UserMatchingRequestDto";
import { LoginErrorCodes } from "@data-types/user/LoginErrorCodes";
import { history, localizationService, redirect } from "@state";
import { getCurrentCulture } from "@utils";
import { StorageStateKeysEnum } from "@utils/storage";
import { action, observable, runInAction } from "mobx";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { UserAuthStore } from "@state/shared";
import { PlatformRegistrationApiService } from "@api-services/platform";
import { UserMatchingDto } from "@api-types/user/UserMatchingDto";
import { LoaderStore } from "@state/stores/common";
import { registrationValidationSingle } from "@utils/membership/registrationValidation";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const UserService = lazy(loadFailPath, ()=>import("@services/user/UserService"))

export class UserMatchingStore {
	@observable isDenyConfirmationPopupActive: boolean = false;
	@observable isRegistrationSuccess: boolean = false;
	@observable userRegistrationMatchingData: {
		possibleMatches: string[];
		tempInfoId: string;
	} | null = null;
	@observable hasSelectedAnOption: boolean = false;

	userStore: UserAuthStore;
	loaderStore: LoaderStore;

	constructor(userStore: UserAuthStore) {
		this.userStore = userStore;
		this.loaderStore = new LoaderStore();
	}

	@action.bound
	public async checkUserMatchingState(): Promise<void> {
		if (this.userStore.loginStore.isUserMatchingInProgress) {
			await this.getMatchingUsersList();
		}
	}

	@action.bound
	public async getMatchingUsersList(): Promise<void> {
		const matchingDataResponse = await (
			await UserService
		).default.findMatchingData();

		if (matchingDataResponse != null) {
			runInAction(() => {
				this.userStore.loginStore.userMatchingDataList =
					matchingDataResponse;
			});
		}
	}

	@action.bound
	public activateDenyConfirmationPopup() {
		this.isDenyConfirmationPopupActive = true;
	}

	@action.bound
	public deactivateDenyConfirmationPopup() {
		this.isDenyConfirmationPopupActive = false;
	}

	@action.bound
	public setHasSelectedAnOption(hasSelectedAnOption: boolean) {
		this.hasSelectedAnOption = hasSelectedAnOption;
	}

	@action.bound
	public setUserRegistrationMatchingData(userMatchingData: {
		possibleMatches: string[];
		tempInfoId: string;
	}) {
		this.userRegistrationMatchingData = userMatchingData;

		this.userStore.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.USER_MATCHING_DATA,
			JSON.stringify(userMatchingData)
		);
	}

	@action.bound
	public getUserRegistrationMatchingData() {
		if (this.userRegistrationMatchingData == null) {
			this.userRegistrationMatchingData = JSON.parse(
				this.userStore.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.USER_MATCHING_DATA
				) as string
			);
		}
	}

	@action.bound
	public setUserMatchingState(flag: boolean): void {
		if (!flag) {
			this.userStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.USER_MATCHING
			);
		} else {
			this.userStore.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.USER_MATCHING,
				`${flag}`
			);
		}

		this.userStore.loginStore.isUserMatchingInProgress = flag;
		if (this.userStore.loginStore.isUserMatchingInProgress) {
			this.getMatchingUsersList();
		}
	}

	@action.bound
	async resolveUserMatchingData(errorCode?: number): Promise<void> {
		if (errorCode != null) {
			if (errorCode === LoginErrorCodes.matchingAvailable) {
				const matchingDataResponse = await (
					await UserService
				).default.findMatchingData();

				if (matchingDataResponse != null) {
					this.setUserMatchingState(true);
					runInAction(() => {
						this.userStore.loginStore.userMatchingDataList =
							matchingDataResponse;
					});
				}
			}
		}
	}

	@action.bound
	public async onUserMatchingCancel(): Promise<void> {
		this.clearUserMatchingList();
		this.setUserMatchingState(false);

		await (await UserService).default.denyUserMatching();
	}

	@action.bound
	public clearUserMatchingList(): void {
		this.userStore.loginStore.userMatchingDataList = [];
	}

	@action.bound
	public async onUserMatchingConfirm(
		userData: UserMatchingRequestDto
	): Promise<void> {
		this.userStore.loginStore.isLoginWorking = true;
		try {
			const stayLoggedInState =
				this.userStore.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.SLN
				);
			userData.stayLoggedIn = stayLoggedInState === "true" ? true : false;
			userData.primaryUserId = this.userStore.user?.id!;

			const response = await (
				await UserService
			).default.confirmUserMatching(userData);

			if (response != null) {
				if (response.matchingErrorCode != null) {
					// 400168 - user temp lock
					// 400197 - oasis block
					// 400166 - temp lock (+ u message propu imati ćete broj minuta)
					// 400167 - user lock with reason
					// 400111 - locked user
					// 400112 - not approved
					// 400202 - not valid
					this.userStore.setUserAccounts(response.userAccounts);
					if (response.matchingErrorCode === "400111") {
						this.userStore.rootStore.notificationStore.error(
							localizationService.t(
								"specific:MEMBERSHIP.LOGIN.ERR_HANDLING.USER_LOCKED"
							)
						);
					}
					if (response.matchingErrorCode === "400112") {
						this.userStore.rootStore.notificationStore.error(
							localizationService.t(
								"login:ERR_HANDLING.USER_NOT_VERIFIED"
							)
						);
					}
					if (response.matchingErrorCode === "400166") {
						this.userStore.rootStore.notificationStore.error(
							localizationService.t(
								"login:ERR_HANDLING.USER_TEMP_LOCK_TIME",
								{ 0: response.message }
							)
						);
					}
					if (response.matchingErrorCode === "400167") {
						this.userStore.rootStore.notificationStore.error(
							localizationService.t(
								"specific:MEMBERSHIP.LOGIN.ERR_HANDLING.USER_LOCKED"
							)
						);
					}
					if (response.matchingErrorCode === "400168") {
						this.userStore.rootStore.notificationStore.error(
							localizationService.t(
								"login:ERR_HANDLING.USER_TEMP_LOCK"
							)
						);
					}
					if (response.matchingErrorCode === "400197") {
						this.userStore.rootStore.notificationStore.error(
							localizationService.t(
								"login:ERR_HANDLING.OASIS_GAMING_EXCLUSION"
							)
						);
					}
					if (response.matchingErrorCode === "400202") {
						this.userStore.rootStore.notificationStore.error(
							localizationService.t(
								"login:ERR_HANDLING.USER_DOCUMENTS_NOT_VERIFIED"
							)
						);
					}
				} else {
					this.userStore.login(response);
				}
			}

			this.clearUserMatchingList();
			this.setUserMatchingState(false);
		} catch (error) {
			if (error.errorCode === 400175) {
				// WRONG PASSWORD TOO MANY TIMES
				this.userStore.rootStore.userAuthStore.logoutUser();
				history.push(
					`/${getCurrentCulture()}/auth/login?isTooManyAttempts=true`
				);
				// INVALID PASSWORD TRY AGAIN
			} else if (error.errorCode === 400176) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING.INVALID_PASSWORD"
					)
				);
			}
			// TRIED TO LOG IN INTO THE SAME ACCOUNT
			else if (error.errorCode === 400223) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING.LOGGED_IN_USER"
					)
				);
			}
			// USER DOESN'T EXIST
			else if (error.errorCode === 400149) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING.INVALID_USER"
					)
				);
			}
			// MATCHING FAILED - GENERIC ERROR
			else if (error.errorCode === 400224) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t("USER_MATCHING.GENERIC_ERROR")
				);
			} else {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t("USER_MATCHING.GENERIC_ERROR")
				);
			}
		}

		runInAction(() => {
			this.userStore.loginStore.isLoginWorking = false;
		});
	}

	@action.bound
	public confirmUserMatchingAndCompleteRegistration = async (
		userMatchingDto: UserMatchingDto,
		isDeny: boolean
	) => {
		this.loaderStore.suspend();
		try {
			if (this.userRegistrationMatchingData?.tempInfoId) {
				userMatchingDto.tempRegistrationId =
					this.userRegistrationMatchingData?.tempInfoId;
			}

			await PlatformRegistrationApiService.confirmUserMatchingAndCompleteRegistration(
				userMatchingDto
			);

			if (isDeny) {
				this.setHasSelectedAnOption(true);
				redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
				return;
			}

			this.isRegistrationSuccess = true;

			this.setHasSelectedAnOption(true);

			redirect(`/${getCurrentCulture()}/auth/register`);
		} catch (error) {
			console.log(error);

			this.setHasSelectedAnOption(false);

			//UNKNOWN USERNAME
			if (error?.data?.errorCode === 400102) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"ERR_HANDLING.INVALID_PASS_OR_USERNAME_POPUP"
					)
				);
			}

			//WRONG PASSWORD
			else if (error?.data?.errorCode === 400176) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING.INVALID_PASSWORD"
					)
				);
			}

			//USER NOT FOUND
			else if (error?.data?.errorCode === 400149) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING.INVALID_USER"
					)
				);
			}

			//USER MATCHING SAME ACCOUNT LOGIN
			else if (error?.data?.errorCode === 400223) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING.LOGGED_IN_USER"
					)
				);

				//WRONG PASSWORD TOO MANY TIMES
			} else if (error?.data?.errorCode === 400175) {
				this.onDispose();

				this.userStore.rootStore.notificationStore.error(
					localizationService.t(
						"user-account:LUGAS.WRONG_PASSWORD_TOO_MANY_TIMES"
					)
				);
			}

			//DISABLED MULTIWALLETS ON ORGANIZATIONAL STRUCTURE
			else if (error?.data?.errorCode === 400313) {
				this.userStore.rootStore.notificationStore.error(
					localizationService.t("USER_MATCHING.GENERIC_ERROR")
				);
			} else {
				registrationValidationSingle(error);
			}

			this.isRegistrationSuccess = false;
		} finally {
			this.loaderStore.resume();
		}
	};

	@action.bound
	public denyUserMatching = async () => {
		await this.confirmUserMatchingAndCompleteRegistration(
			{
				isMatchingConfirmed: false,
				username: "",
				tempRegistrationId: "",
				password: "",
			},
			true
		);

		this.deactivateDenyConfirmationPopup();
	};

	@action.bound
	setIsRegistrationSuccess(isRegistrationSuccess: boolean) {
		this.isRegistrationSuccess = isRegistrationSuccess;
	}

	@action.bound
	onDispose() {
		this.userRegistrationMatchingData = null;
		this.isDenyConfirmationPopupActive = false;
		this.hasSelectedAnOption = false;

		this.userStore.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.USER_MATCHING_DATA
		);
	}
}
